import { Injectable } from '@angular/core';
import { BaseService, IKeyValuePair } from './base.service';
import { Device } from '../../models/device.model';
import { Observable } from 'rxjs';
import { AuthorizationService } from '../authorization.service';
import { MessageService } from '../message.service';
import { CacheService } from '../cache.service';
import { HttpClient } from '@angular/common/http';
import { DateHelper } from '../../utils/date-helper';
import { Constants } from '../../models/constants.model';
import { StatusId } from '../../models/status-id';
import { AvailableDevice } from '../../models/available-device';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../language.service';
import { IAvailableRessourceParams } from './room.service';

@Injectable({
  providedIn: 'root'
})
export class DeviceService extends BaseService<Device>  {

  protected endpointUri = 'RoomReservation/Devices';

  constructor(
    http: HttpClient,
    authorization: AuthorizationService,
    messages: MessageService,
    cache: CacheService,
    private language: LanguageService,
    private translation: TranslateService
  ) {
    super(http, authorization, messages, cache);
  }

  getAll(): Observable<Device[]> {
    throw new Error('Method not implemented.');
  }

  get(id: string): Observable<any> {
    return this.getRequest(id);
  }

  add(entity: Device, headers: IKeyValuePair<string, string>[]): Observable<any> {
    throw new Error('Method not implemented.');
  }

  update(entity: Device): Observable<any> {
    throw new Error('Method not implemented.');
  }

  delete(id: string): Observable<{}> {
    throw new Error('Method not implemented.');
  }

  getAvailableDevices(params: IAvailableDevicesParams): Observable<any> {
    return this.getRequest(null, this.endpointUri + '/Available', this.mapAvailableDevicesParams(params));
  }

  getOccupanciesById(roomId: number, dateFrom: Date, dateTo: Date): Observable<any> {
    return this.getRequest(null, this.endpointUri + '/' + String(roomId) + '/Occupancies', [
      { key: 'dateFrom', value: String(DateHelper.getUnixTime(dateFrom)) },
      { key: 'dateTo', value: String(DateHelper.getUnixTime(dateTo)) }
    ]);
  }

  /**
  * Map search-params from IAvailableDevicesParams
  *
  * @private
  * @param {IAvailableDevicesParams} params
  * @returns {IKeyValuePair[]}
  * @memberof DeviceService
  */
  private mapAvailableDevicesParams(params: IAvailableDevicesParams): IKeyValuePair<string, string>[] {
    return [
      { key: nameof<IAvailableDevicesParams>('idDevice'), value: params.idDevice.join(',') },
      { key: nameof<IAvailableDevicesParams>('availableFrom'), value: String(DateHelper.getUnixTime(params.availableFrom)) },
      { key: nameof<IAvailableDevicesParams>('availableTo'), value: String(DateHelper.getUnixTime(params.availableTo)) },
      { key: nameof<IAvailableDevicesParams>('availableOnly'), value: params.availableOnly ? '1' : null },
      { key: 'designations', value: params.ressourceNumber.join(',') },
      { key: nameof<IAvailableDevicesParams>('timespan'), value: params.timespan ? String(params.timespan * 60) : '' }
    ];
  }

  /**
   * Returns the first warning if the AvailableDevice ca not be reserved
   *
   * @param {AvailableDevice} device
   * @param {Date} searchDate
   * @returns {string}
   * @memberof DeviceService
   */
  public getFirstWarning(device: AvailableDevice, searchDate: Date): Observable<string> {
    // todo tosc: do all this messages exist?
    // status
    switch (device.StatusId) {
      case StatusId.rrGesperrt:
        return this.translation.get('DEVICE_STATE_CLOSED');
      case StatusId.rrGesperrtBis:
        if (device.StatusDate > searchDate) {
          return this.translation.get('DEVICE_STATE_CLOSED_UNTIL', { date: this.language.formatDate(device.StatusDate) });
        }
        break;
      case StatusId.rrReservierbarBis:
        if (device.StatusDate < searchDate) {
          return this.translation.get('DEVICE_STATE_OPEN_UNTIL', { date: this.language.formatDate(device.StatusDate) });
        }
        break;
      case StatusId.rrReservierbar: // ok
        break;
      default:
        console.warn(`status unknown: (${device.StatusId} - ${device.Status})`);
        break;
    }

    // c)	hat das Attribut «Belegung erlaubt» inaktiviert:
    if (!device.StatusAllowOccupation) {
      return this.translation.get('DEVICE_STATE_CLOSED');
    }

    // reservationstyp
    switch (device.ReservationRightId) {
      case Constants.ReservationsRecht.Reservable:
        // ok
        break;
      case Constants.ReservationsRecht.ProvReservable:
        return this.translation.get('DEVICE_PERMISSION_PROV');
      case Constants.ReservationsRecht.Requestable:
        return this.translation.get('DEVICE_PERMISSION_REQUEST');
      case Constants.ReservationsRecht.Visible:
      case Constants.ReservationsRecht.None:
        return this.translation.get('DEVICE_NO_PERMISSION_OCCUPY');
      default:
        console.log(device);
        throw Error('invalid reservation type: ' + device.ReservationRightId);
    }

    // keine Arbeitszeit
    if (!device.IsWorkingDay) {
      return this.translation.get('DEVICE_STATE_NO_WORKDAY', { date: this.language.formatDate(searchDate) });
    }

    return null;
  }

}

/**
 * Paramter object for getAvailableDevices
 *
 * @export
 * @interface IAvailableDevicesParams
 */
export interface IAvailableDevicesParams extends IAvailableRessourceParams {
  idDevice: number[];

}
